import React, { useState } from "react"
import { faCheck, faClose, faLink, faPlus, faSpinner, faTrashCan } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { useTranslation } from "react-i18next"
import Animate, { AnimationTypes } from "../Animate/Animate"
import { ButtonIcon } from "../Button/Button"
import InputField from "../InputField/InputField"
import ModalPortal from "../Modal/ModalPortal"
import { TitlePageResponsiveIconSmall } from "../Titles/Titles"
import THEME from "../../configuration/Theme"
import { SIZE } from "../../configuration/Sizes"
import defaultStyle from "../../styles/defaultPageStyle.module.css"

interface ExternalImageUrlsPopupProps {
  onClose: () => void
  onConfirm: (urls: string[]) => Promise<boolean>
}

export const ExternalImageUrlsPopup: React.FC<ExternalImageUrlsPopupProps> = ({ onClose, onConfirm }) => {
  const { t } = useTranslation("catalogProductEntity")

  const [urls, setUrls] = useState([""])

  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleChange = (index: number, value: string) => {
    setUrls((current) => current.map((url, currentIndex) => (currentIndex === index ? value : url)))
  }

  const handleConfirm = async () => {
    const normalizedUrls = urls.map((url) => url.trim()).filter((url) => url.length > 0)

    if (normalizedUrls.length === 0 || isSubmitting) {
      return
    }

    setIsSubmitting(true)

    const success = await onConfirm(normalizedUrls)

    setIsSubmitting(false)

    if (success) {
      onClose()
    }
  }

  return (
    <ModalPortal>
      <div className={defaultStyle.overlay}>
        <Animate type={AnimationTypes.slideUpIn} duration="0.3s">
          <section className={defaultStyle.alert}>
            <TitlePageResponsiveIconSmall
              title={t("label_external_image_title")}
              icon={faLink}
              iconColor={THEME.colors.iconBGColor}
              children={
                <div className={defaultStyle.ButtonsActions}>
                  <ButtonIcon
                    sizeNumber={SIZE.buttonCloseIconSize}
                    bgColor={THEME.colors.buttonBGCloseColor}
                    iconColor={THEME.colors.buttonFGCloseColor}
                    icon={faClose}
                    onClick={onClose}
                    disabled={isSubmitting}
                    size="small"
                    descriptionKey="interactionDescriptions:components.imageLibrary.externalImageUrlsPopup.button.onClose"
                  />
                </div>
              }
            />

            <Animate type={AnimationTypes.fadeInTraslate} duration="1.5s">
              <div className="space-y-3 p-3">
                <p className="text-sm text-slate-600">{t("label_external_image_hint")}</p>

                {urls.map((url, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <InputField
                      type="link"
                      value={url}
                      onChange={(event) => handleChange(index, event.target.value)}
                      placeholder={t("label_external_image_url_placeholder", { index: index + 1 })}
                      maxLength={300}
                      disable={isSubmitting}
                      autoComplete="url"
                      descriptionKey="interactionDescriptions:components.imageLibrary.externalImageUrlsPopup.input.externalImageUrlPlaceholder"
                    />

                    {urls.length > 1 && (
                      <ButtonIcon
                        icon={faTrashCan}
                        sizeNumber={SIZE.buttonCloseIconSize}
                        bgColor={THEME.colors.buttonBGCloseColor}
                        iconColor={THEME.colors.buttonFGCloseColor}
                        onClick={() =>
                          setUrls((current) => current.filter((_, currentIndex) => currentIndex !== index))
                        }
                        disabled={isSubmitting}
                        size="small"
                        descriptionKey="interactionDescriptions:components.imageLibrary.externalImageUrlsPopup.button.setUrlsCurrentCurrentFilterCurrentIndexCurrentIndexIndex"
                      />
                    )}
                  </div>
                ))}

                <div className="grid gap-3 border-t border-slate-100 pt-3 sm:grid-cols-2">
                  <button
                    type="button"
                    onClick={() => setUrls((current) => (current.length >= 20 ? current : [...current, ""]))}
                    disabled={isSubmitting || urls.length >= 20}
                    className="inline-flex min-h-12 items-center justify-center gap-2 rounded-xl border-2 border-dashed border-orange-200 bg-orange-50 px-4 py-3 text-sm font-black text-orange-700 transition-all hover:border-orange-400 hover:bg-orange-100 disabled:cursor-not-allowed disabled:opacity-50"
                    data-interaction-description-key="interactionDescriptions:components.imageLibrary.externalImageUrlsPopup.button.setUrlsCurrentCurrentLength20CurrentCurrent"
                  >
                    <FontAwesomeIcon icon={faPlus} />
                    {t("button_add_another_external_image")}
                  </button>

                  <button
                    type="button"
                    onClick={handleConfirm}
                    disabled={isSubmitting || urls.every((url) => url.trim().length === 0)}
                    className="inline-flex min-h-12 items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-orange-500 to-orange-600 px-4 py-3 text-sm font-black text-white shadow-lg shadow-orange-200 transition-all hover:-translate-y-0.5 hover:from-orange-600 hover:to-orange-700 hover:shadow-xl disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:translate-y-0"
                    data-interaction-description-key="interactionDescriptions:components.imageLibrary.externalImageUrlsPopup.button.handleConfirm"
                  >
                    <FontAwesomeIcon icon={isSubmitting ? faSpinner : faCheck} spin={isSubmitting} />
                    {t(isSubmitting ? "button_registering_external_images" : "button_register_external_images")}
                  </button>
                </div>
              </div>
            </Animate>
          </section>
        </Animate>
      </div>
    </ModalPortal>
  )
}
